// /app/frontend/src/pages/public/ReadinessAssessmentPage.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, ArrowRight, Send, CheckCircle2, Loader2, Save,
  Shield, Users, Database, Lock, Building2, AlertTriangle,
  FileText, Clock, BookOpen
} from 'lucide-react';
import { toast } from 'sonner';

import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Textarea } from '../../components/ui/textarea';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '../../components/ui/alert-dialog';

import { readinessAPI, tempGuestAPI } from '../../services/api';

// Section icons
const SECTION_ICONS = {
  governance: Shield,
  policies: BookOpen,
  data_inventory: Database,
  transparency: FileText,
  rights: Users,
  vendors: Building2,
  security: Lock,
  dpia: Shield,
  retention: Clock,
  breach: AlertTriangle
};

// Section descriptions
const SECTION_DESCRIPTIONS = {
  governance: "Who is accountable for data protection in your organisation.",
  policies: "Written rules, training, and staff awareness.",
  data_inventory: "What personal data you hold and why (ROPA).",
  transparency: "How you inform people about data use.",
  rights: "How you handle access, deletion, and correction requests.",
  vendors: "How you manage third-party data processors.",
  security: "Technical and organisational security measures.",
  dpia: "Privacy impact assessments and privacy by design.",
  retention: "How long you keep data and how you dispose of it.",
  breach: "How you detect, report, and manage data breaches."
};

// Answer options
const ANSWER_OPTIONS = [
  { key: 'yes', label: 'Yes', description: 'This control or practice is fully in place', color: 'bg-green-500 hover:bg-green-600' },
  { key: 'partial', label: 'Partial', description: 'Partially implemented or inconsistently applied', color: 'bg-yellow-500 hover:bg-yellow-600' },
  { key: 'no', label: 'No', description: 'Not implemented or not in place', color: 'bg-red-500 hover:bg-red-600' },
  { key: 'na', label: 'N/A', description: 'Not applicable to our organisation', color: 'bg-gray-500 hover:bg-gray-600' }
];

export default function ReadinessAssessmentPage({ guestMode = false }) {
  const { token } = useParams();
  const navigate = useNavigate();
  const guestUser = guestMode ? JSON.parse(localStorage.getItem('guest_user') || '{}') : null;
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [assessment, setAssessment] = useState(null);
  const [answers, setAnswers] = useState({});
  const [comments, setComments] = useState({});
  const [lastSaved, setLastSaved] = useState(null);
  const [saveConfirmed, setSaveConfirmed] = useState(false);
  const [showSubmitDialog, setShowSubmitDialog] = useState(false);
  const [submitResult, setSubmitResult] = useState(null);
  const [showResumeBanner, setShowResumeBanner] = useState(false);

  // Navigation state
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  
  const saveTimeoutRef = useRef(null);

  useEffect(() => {
    if (guestMode) { setLoading(false); return; }
    loadAssessment();
  }, [token]);

  useEffect(() => {
    // Keyboard shortcuts
    const handleKeyDown = (e) => {
      if (e.key >= '1' && e.key <= '4') {
        const optionIndex = parseInt(e.key) - 1;
        if (optionIndex < ANSWER_OPTIONS.length) {
          handleAnswer(ANSWER_OPTIONS[optionIndex].key);
        }
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentSectionIndex, currentQuestionIndex, assessment]);

  const loadAssessment = async () => {
    try {
      setLoading(true);
      const [assessmentRes, responsesRes] = await Promise.all([
        readinessAPI.getPublic(token),
        readinessAPI.getPublicResponses(token).catch(() => ({ data: [] }))
      ]);

      const assessmentData = assessmentRes.data;
      setAssessment(assessmentData);

      // Load existing responses
      const existingAnswers = {};
      const existingComments = {};
      responsesRes.data.forEach(r => {
        existingAnswers[r.question_id] = r.answer;
        if (r.comment) existingComments[r.question_id] = r.comment;
      });
      setAnswers(existingAnswers);
      setComments(existingComments);

      // Resume detection: if user has prior answers, show banner and auto-navigate
      if (Object.keys(existingAnswers).length > 0) {
        setShowResumeBanner(true);

        // Auto-navigate to first unanswered question
        for (let si = 0; si < assessmentData.sections.length; si++) {
          const sqs = assessmentData.questions.filter(q => q.section_id === assessmentData.sections[si].id);
          const firstUnanswered = sqs.findIndex(q => !existingAnswers[q.question_id]);
          if (firstUnanswered !== -1) {
            setCurrentSectionIndex(si);
            setCurrentQuestionIndex(firstUnanswered);
            break;
          }
        }
      }
      
    } catch (error) {
      console.error('Error loading assessment:', error);
      if (error.response?.status === 410) {
        toast.error('This assessment has expired');
      } else if (error.response?.status === 400) {
        toast.error('This assessment has already been submitted');
      } else {
        toast.error('Failed to load assessment');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAnswer = async (answerKey) => {
    const question = currentQuestion;
    if (!question) return;
    
    const newAnswers = { ...answers, [question.question_id]: answerKey };
    setAnswers(newAnswers);
    
    // Auto-save after brief delay
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => {
      saveResponse(question.question_id, answerKey, comments[question.question_id]);
    }, 500);
  };

  const handleCommentChange = (questionId, comment) => {
    setComments(prev => ({ ...prev, [questionId]: comment }));
  };

  const saveResponse = async (questionId, answer, comment) => {
    try {
      setSaving(true);
      await readinessAPI.saveResponse(token, {
        question_id: questionId,
        answer: answer,
        comment: comment || null
      });
      setLastSaved(new Date());
      setSaveConfirmed(true);
      setTimeout(() => setSaveConfirmed(false), 3000);
    } catch (error) {
      console.error('Error saving response:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleNext = () => {
    const sectionQuestions = getSectionQuestions(currentSectionIndex);
    
    if (currentQuestionIndex < sectionQuestions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    } else if (currentSectionIndex < assessment.sections.length - 1) {
      setCurrentSectionIndex(prev => prev + 1);
      setCurrentQuestionIndex(0);
    }
  };

  const handlePrev = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
    } else if (currentSectionIndex > 0) {
      setCurrentSectionIndex(prev => prev - 1);
      const prevSectionQuestions = getSectionQuestions(currentSectionIndex - 1);
      setCurrentQuestionIndex(prevSectionQuestions.length - 1);
    }
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      if (guestMode) {
        await tempGuestAPI.submitReadiness({ responses: answers, assessment_name: 'Readiness Assessment' });
        setShowSubmitDialog(false);
        toast.success('Assessment submitted successfully!');
        navigate('/guest/portal');
        return;
      }
      const result = await readinessAPI.submit(token);
      setSubmitResult(result.data);
      setShowSubmitDialog(false);
      toast.success('Assessment submitted successfully!');
    } catch (error) {
      console.error('Error submitting:', error);
      toast.error('Failed to submit assessment');
    } finally {
      setSubmitting(false);
    }
  };

  const getSectionQuestions = (sectionIndex) => {
    if (!assessment) return [];
    const section = assessment.sections[sectionIndex];
    return assessment.questions.filter(q => q.section_id === section.id);
  };

  const getOverallProgress = () => {
    if (!assessment) return { answered: 0, total: 0, percent: 0 };
    const answered = Object.keys(answers).length;
    const total = assessment.total_questions;
    return { answered, total, percent: total > 0 ? Math.round((answered / total) * 100) : 0 };
  };

  const getSectionProgress = (sectionIndex) => {
    if (!assessment) return { answered: 0, total: 0, percent: 0, complete: false };
    const section = assessment.sections[sectionIndex];
    const sectionQuestions = assessment.questions.filter(q => q.section_id === section.id);
    const answered = sectionQuestions.filter(q => answers[q.question_id]).length;
    const total = sectionQuestions.length;
    return { 
      answered, 
      total, 
      percent: total > 0 ? Math.round((answered / total) * 100) : 0,
      complete: answered === total
    };
  };

  // Loading
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-primary" />
          <p className="text-muted-foreground">Loading assessment...</p>
        </div>
      </div>
    );
  }

  // Error/Not found
  if (!assessment) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <AlertTriangle className="h-12 w-12 mx-auto mb-4 text-destructive" />
            <h2 className="text-xl font-semibold mb-2">Invalid Link</h2>
            <p className="text-muted-foreground">
              This assessment link is invalid, expired, or has already been submitted.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Submitted result
  if (submitResult) {
    return (
      <div className="min-h-screen bg-background">
        <div className="bg-gradient-to-r from-slate-800 to-slate-900 text-white py-8 px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-2xl font-bold">Data Protection Readiness Assessment</h1>
            <p className="text-slate-300 mt-1">{assessment.entity_name}</p>
          </div>
        </div>
        <div className="max-w-2xl mx-auto p-6">
          <Card className="text-center py-12">
            <CardContent>
              <CheckCircle2 className="h-16 w-16 mx-auto mb-4 text-green-500" />
              <h2 className="text-2xl font-bold mb-2">Assessment Submitted</h2>
              <p className="text-muted-foreground mb-6">Thank you for completing this assessment.</p>
              <div className="bg-muted rounded-lg p-6 inline-block">
                <p className="text-sm text-muted-foreground mb-1">Your Score</p>
                <p className="text-4xl font-bold text-primary">{submitResult.overall_score?.toFixed(0)}%</p>
                <p className="text-sm text-muted-foreground">{submitResult.overall_rating_label}</p>
              </div>
              <p className="mt-6 text-sm text-muted-foreground">
                Your consultant will review the results and follow up with you shortly.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const currentSection = assessment.sections[currentSectionIndex];
  const sectionQuestions = getSectionQuestions(currentSectionIndex);
  const currentQuestion = sectionQuestions[currentQuestionIndex];
  const overallProgress = getOverallProgress();
  const sectionProgress = getSectionProgress(currentSectionIndex);
  const SectionIcon = SECTION_ICONS[currentSection?.id] || Shield;
  const canSubmit = overallProgress.answered === overallProgress.total;
  const isLastQuestion = currentSectionIndex === assessment.sections.length - 1 && 
                         currentQuestionIndex === sectionQuestions.length - 1;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-800 to-slate-900 text-white py-6 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-bold">Data Protection Readiness Assessment</h1>
              <p className="text-slate-300 text-sm">{assessment.entity_name}</p>
            </div>
            <div className="flex items-center gap-2">
              {saving && (
                <span className="text-sm text-slate-400 flex items-center gap-1">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  Saving...
                </span>
              )}
              {saveConfirmed && !saving && (
                <span className="text-sm text-green-400 flex items-center gap-1">
                  <CheckCircle2 className="h-4 w-4" />
                  Saved
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="bg-slate-800 pb-4 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-slate-400">Overall Progress</span>
            <span className="text-sm text-slate-400">{overallProgress.percent}% ({overallProgress.answered}/{overallProgress.total})</span>
          </div>
          <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
            <div
              className="h-full bg-primary transition-all duration-300"
              style={{ width: `${overallProgress.percent}%` }}
            />
          </div>
        </div>
      </div>

      {/* Resume Banner */}
      {showResumeBanner && (
        <div className="bg-blue-600/20 border-b border-blue-500/30 px-6 py-3">
          <div className="max-w-4xl mx-auto flex items-center justify-between">
            <span className="text-sm text-blue-300 flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4" />
              You have answered {overallProgress.answered} of {overallProgress.total} questions. Continuing from where you left off.
            </span>
            <button
              onClick={() => setShowResumeBanner(false)}
              className="text-blue-400 hover:text-blue-200 text-xs font-medium"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      {/* Main Content with Sidebar */}
      <div className="max-w-5xl mx-auto p-3 sm:p-4 md:p-6">
        <div className="flex gap-0 md:gap-6">
          {/* Desktop Sidebar */}
          <aside className="hidden md:block w-56 flex-shrink-0">
            <div className="sticky top-6 space-y-2">
              <h3 className="font-semibold text-sm mb-4">Sections</h3>
              {assessment.sections.map((section, index) => {
                const progress = getSectionProgress(index);
                const Icon = SECTION_ICONS[section.id] || Shield;
                const isActive = index === currentSectionIndex;
                return (
                  <div key={section.id}>
                    <button
                      onClick={() => {
                        setCurrentSectionIndex(index);
                        setCurrentQuestionIndex(0);
                      }}
                      className={`w-full flex items-center gap-3 p-3 rounded-lg border-l-4 transition-all text-left text-sm ${
                        isActive
                          ? 'border-l-primary bg-primary/10 text-primary font-medium'
                          : progress.complete
                            ? 'border-l-green-500 hover:bg-muted'
                            : 'border-l-transparent hover:bg-muted'
                      }`}
                    >
                      <Icon className={`h-4 w-4 flex-shrink-0 ${progress.complete ? 'text-green-500' : ''}`} />
                      <span className="flex-1 truncate">{section.title}</span>
                      {progress.complete ? (
                        <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0" />
                      ) : progress.answered > 0 ? (
                        <span className="text-xs text-muted-foreground flex-shrink-0">{progress.answered}/{progress.total}</span>
                      ) : null}
                    </button>
                    {isActive && (
                      <p className="text-xs text-muted-foreground px-3 py-2 italic">
                        {SECTION_DESCRIPTIONS[section.id]}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </aside>

          {/* Main Question Content */}
          <div className="flex-1 min-w-0 space-y-6">
            {/* Section Header */}
        <Card>
          <CardContent className="p-3 sm:p-4 pt-3 sm:pt-6">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
              <div className="w-fit p-2 sm:p-3 bg-primary/10 rounded-lg flex-shrink-0">
                <SectionIcon className="h-5 sm:h-6 w-5 sm:w-6 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                  <h2 className="text-base sm:text-lg font-semibold break-words">{currentSection?.title}</h2>
                  <Badge variant="outline" className="w-fit">{sectionProgress.percent}%</Badge>
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 text-xs sm:text-sm text-muted-foreground mt-1">
                  <span>Question {currentQuestionIndex + 1} of {sectionQuestions.length}</span>
                  <span className="hidden sm:inline">•</span>
                  <span>Section {currentSectionIndex + 1} of {assessment.sections.length}</span>
                </div>
              </div>
            </div>
            <Progress value={sectionProgress.percent} className="mt-3 sm:mt-4 h-2" />
          </CardContent>
        </Card>

        {/* Question Card */}
        <Card>
          <CardContent className="p-3 sm:p-4 pt-3 sm:pt-6 space-y-4 sm:space-y-6">
            {/* Question */}
            <div>
              <Badge variant="outline" className="mb-2 sm:mb-3">Q{currentQuestionIndex + 1}</Badge>
              <h3 className="text-base sm:text-lg">{currentQuestion?.question_text}</h3>
            </div>

            {/* Answer Options */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              {ANSWER_OPTIONS.map((option, index) => {
                const isSelected = answers[currentQuestion?.question_id] === option.key;
                const isPartialOption = option.key === 'partial';
                const hasHint = isPartialOption && currentQuestion?.partial_hint;

                return (
                  // `group` is scoped per-option so hover on this option surfaces this option's hint.
                  <div key={option.key} className="flex flex-col group">
                    <button
                      onClick={() => handleAnswer(option.key)}
                      className={`p-3 sm:p-4 rounded-lg border-2 text-left transition-all ${
                        isSelected
                          ? 'border-primary bg-primary/10'
                          : 'border-border bg-card hover:border-muted-foreground/50'
                      }`}
                    >
                      <div className="flex items-start gap-2 sm:gap-3">
                        <span className={`flex-shrink-0 w-6 h-6 sm:w-7 sm:h-7 rounded-full flex items-center justify-center text-xs sm:text-sm font-medium ${
                          isSelected ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
                        }`}>
                          {index + 1}
                        </span>
                        <div>
                          <p className="font-medium text-sm sm:text-base">{option.label}</p>
                          <p className="text-xs sm:text-sm text-muted-foreground">{option.description}</p>
                        </div>
                      </div>
                    </button>

                    {/* Partial Hint (spec Improvement 2):
                          Desktop: visible on hover of this option only.
                          Mobile (<md): always visible (hover is unreliable on touch). */}
                    {hasHint && (
                      <div
                        className="mt-2 px-2 py-1 text-xs italic text-gray-500 block md:hidden md:group-hover:block"
                      >
                        {currentQuestion.partial_hint}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Comment */}
            <div>
              <div className="flex items-baseline justify-between mb-2">
                <label className="text-xs sm:text-sm text-muted-foreground">
                  Add a comment (optional)
                </label>
                <span className="text-xs text-muted-foreground">
                  {(comments[currentQuestion?.question_id] || '').length}/1000
                </span>
              </div>
              <Textarea
                value={comments[currentQuestion?.question_id] || ''}
                onChange={(e) => handleCommentChange(currentQuestion?.question_id, e.target.value)}
                onBlur={() => {
                  if (answers[currentQuestion?.question_id]) {
                    saveResponse(
                      currentQuestion?.question_id,
                      answers[currentQuestion?.question_id],
                      comments[currentQuestion?.question_id]
                    );
                  }
                }}
                placeholder="Any additional context or clarification..."
                rows={2}
                maxLength={1000}
                className="text-sm"
              />
            </div>

            {/* Navigation */}
            <div className="flex flex-col-reverse sm:flex-row items-center justify-between gap-2 pt-4 border-t">
              <Button
                variant="outline"
                onClick={handlePrev}
                disabled={currentSectionIndex === 0 && currentQuestionIndex === 0}
                size="sm"
                className="w-full sm:w-auto"
              >
                <ArrowLeft className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">Previous</span>
                <span className="sm:hidden">Back</span>
              </Button>

              <div className="flex items-center gap-2 w-full sm:w-auto">
                {isLastQuestion ? (
                  <Button
                    onClick={() => setShowSubmitDialog(true)}
                    disabled={!canSubmit}
                    size="sm"
                    className="flex-1 sm:flex-none"
                  >
                    <Send className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                    <span className="hidden sm:inline">Submit</span>
                    <span className="sm:hidden">Done</span>
                  </Button>
                ) : (
                  <Button
                    onClick={handleNext}
                    disabled={!answers[currentQuestion?.question_id]}
                    size="sm"
                    className="flex-1 sm:flex-none"
                  >
                    Next
                    <ArrowRight className="h-3 w-3 sm:h-4 sm:w-4 ml-1 sm:ml-2" />
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

            {/* Mobile Section Strip */}
            <div className="md:hidden w-full overflow-x-auto -mx-3 sm:-mx-4 px-3 sm:px-4">
              <div className="flex gap-2 w-min">
                {assessment.sections.map((section, index) => {
                  const progress = getSectionProgress(index);
                  const Icon = SECTION_ICONS[section.id] || Shield;
                  return (
                    <button
                      key={section.id}
                      onClick={() => {
                        setCurrentSectionIndex(index);
                        setCurrentQuestionIndex(0);
                      }}
                      className={`flex flex-col items-center gap-1 p-2 rounded-lg border whitespace-nowrap transition-all flex-shrink-0 ${
                        index === currentSectionIndex
                          ? 'border-primary bg-primary/10'
                          : progress.complete
                            ? 'border-green-500 bg-green-500/10'
                            : 'border-border'
                      }`}
                    >
                      <Icon className={`h-4 w-4 ${progress.complete ? 'text-green-500' : 'text-muted-foreground'}`} />
                      <span className="text-xs">{progress.percent}%</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Keyboard shortcuts */}
            <div className="text-center text-sm text-muted-foreground">
              <span className="bg-muted px-2 py-1 rounded">1-4</span> to select answer
            </div>
          </div>
        </div>
      </div>

      {/* Submit Dialog */}
      <AlertDialog open={showSubmitDialog} onOpenChange={setShowSubmitDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Submit Assessment?</AlertDialogTitle>
            <AlertDialogDescription>
              You have answered all {overallProgress.total} questions. Once submitted, you cannot modify your responses.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Review Answers</AlertDialogCancel>
            <AlertDialogAction onClick={handleSubmit} disabled={submitting}>
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Submit
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
